import React, { useContext } from "react";
import { defineMessages, useIntl } from "react-intl";
import { Group, Student } from "../../../../../../../interfaces/Session";
import {
    PlaylistItem,
    Playlist,
    PlaylistProgress,
    Assignment,
    PartialClassroomsProgress,
    PlaylistStatus,
} from "../../../../type";
import Recipient from "../Recipient/Recipient";
import { UseQueryResult } from "@tanstack/react-query";
import { sessionStore } from "../../../../../../../contexts/SessionContext";
import SimpleBar from "simplebar-react";
import Loader from "../../../../../../../components/Loader/Loader";
import {
    usePlaylistAssignmentsQuery,
    usePlaylistProgressQuery,
} from "../../../../api/useQueries";
import classNames from "classnames";
import uniqBy from "lodash/uniqBy";
import { Pagetype } from "../../../../../../../interfaces/Config";
import { useUrl } from "../../../../../../../hooks/usePage";
import useOnlineStatus from "../../../../../../../hooks/useOnlineStatus";
import { ROUTES } from "../../../../../../../design-system-components/WorkmodeLayout/const";

import "./PlaylistDetail.scss";
import { Button } from "@evidenceb/athena-common/design-system/Button";

interface PlaylistDetailProps {
    playlist: Playlist<PlaylistItem>;
}

export interface GroupedRecipients {
    name: string;
    students: Student[];
}

const PlaylistDetail = ({ playlist }: PlaylistDetailProps) => {
    const intl = useIntl();
    const {
        session: { classrooms },
    } = useContext(sessionStore);
    const isOnline = useOnlineStatus();

    let progressQuery:
        | Omit<UseQueryResult<PlaylistProgress | undefined>, "refetch">
        | undefined = usePlaylistProgressQuery(playlist.learning_set_id);
    if (progressQuery.isError) {
        progressQuery = undefined;
    }

    const assignmentResult = usePlaylistAssignmentsQuery(
        playlist.learning_set_id
    );
    const classroomsProgress = getClassroomProgress(
        progressQuery,
        assignmentResult,
        classrooms
    );
    const urlMode = useUrl(Pagetype.WORKMODE_PLAYLIST);

    return (
        <div className="playlist-detail">
            <div className="playlist-detail__recipients">
                <Loader
                    isLoading={
                        (progressQuery?.isLoading || progressQuery?.isStale) ??
                        true
                    }
                >
                    {classroomsProgress && (
                        <>
                            {classroomsProgress.length > 0 ? (
                                <StudentsList
                                    updating={progressQuery?.isStale ?? true}
                                    classroomsProgress={classroomsProgress}
                                    playlist={playlist}
                                />
                            ) : (
                                <div>
                                    {playlist.status.status !==
                                    PlaylistStatus.ARCHIVED ? (
                                        <Button
                                            label={intl.formatMessage(
                                                messages.share
                                            )}
                                            icons={[
                                                {
                                                    path: "add",
                                                },
                                            ]}
                                            asRouterLink={{
                                                to: `${urlMode}/${ROUTES.create}/`,
                                            }}
                                            disabled={!isOnline}
                                        />
                                    ) : null}
                                    <p>
                                        {intl.formatMessage(
                                            messages.noRecipients
                                        )}
                                    </p>
                                </div>
                            )}
                        </>
                    )}
                </Loader>
            </div>
        </div>
    );
};

export default PlaylistDetail;

type StudentsListProps = {
    updating: boolean;
    classroomsProgress: PartialClassroomsProgress[];
    playlist: Playlist<PlaylistItem>;
};
const StudentsList = ({
    updating,
    classroomsProgress,
    playlist,
}: StudentsListProps) => {
    const students = uniqBy(
        classroomsProgress.flatMap((group) => group.students_progress),
        "id"
    ).sort((stud1, stud2) => stud1.last_name.localeCompare(stud2.last_name));

    return (
        <div
            className={classNames("playlist-detail__classrooms-container", {
                "playlist-detail__updating": updating,
            })}
        >
            <SimpleBar style={{ height: "100%" }}>
                <div className="group-detail">
                    <ol>
                        {students.map((stud) => (
                            <li className="group-detail__student" key={stud.id}>
                                <Recipient
                                    studentId={stud.id}
                                    exercisesDone={
                                        typeof stud.exercises_done !==
                                        "undefined"
                                            ? stud.exercises_done
                                            : undefined
                                    }
                                    className="playlist-group-detail__recipient"
                                    playlist={playlist}
                                />
                            </li>
                        ))}
                    </ol>
                </div>
            </SimpleBar>
        </div>
    );
};

export const getClassroomProgress = (
    progressQuery:
        | Omit<UseQueryResult<PlaylistProgress | undefined>, "refetch">
        | undefined,
    assignmentQuery: UseQueryResult<Assignment[] | undefined>,
    classrooms?: Group[] | undefined
): undefined | PartialClassroomsProgress[] => {
    const assignment = assignmentQuery.data?.[0];
    if (!classrooms || !assignment) return undefined;
    return classrooms
        .map((classroom) => ({
            id: classroom.id,
            name: classroom.name,
            students_progress: classroom.students
                .filter((student) =>
                    assignment.students.some(
                        (assignedStudent) => assignedStudent.id === student.id
                    )
                )
                .map((student) => ({
                    first_name: student.firstname,
                    last_name: student.lastname,
                    id: student.id,
                    exercises_done: progressQuery?.data?.classrooms_progress
                        .find(
                            (progressClassroom) =>
                                progressClassroom.id === classroom.id
                        )
                        ?.students_progress.find(
                            (progressStudent) =>
                                progressStudent.id === student.id
                        )?.exercises_done,
                })),
        }))
        .filter((classroom) => classroom.students_progress.length > 0);
};

const messages = defineMessages({
    changeButton: {
        id: "playlist-changeButton",
        defaultMessage: "change",
    },
    share: {
        id: "playlist-share",
        defaultMessage: "Share",
    },
    noRecipients: {
        id: "playlist-no-recipients",
        defaultMessage: "This playlist has not recipients yet.",
    },
});
